import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { createRoomObject, deleteRoomObject, getRoomObjects, updateRoomObject, patchRoomObject } from '../api/rooms';
import { getAiInferencePresignedUrl, runAiInference } from '../api/aiInference';
import { addTask } from '../services/taskResolver';
import { pickRoomObjectImageUrl, resolveRoomObjectImageSrcForDisplay } from '../utils/roomObjectImage';

const MAX_FILE_SIZE_BYTES = 8 * 1024 * 1024;
const ACCEPTED_MIME_TYPES = new Set(['image/jpeg', 'image/png']);
const ACCEPTED_EXTENSIONS = ['.jpg', '.jpeg', '.png'];

function isAcceptedImage(file) {
  const name = file?.name?.toLowerCase() || '';
  const hasAllowedExtension = ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
  return ACCEPTED_MIME_TYPES.has(file.type) || hasAllowedExtension;
}

function formatBytes(size) {
  if (!Number.isFinite(size)) return '0 B';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

const TRANSPARENCY_CHECK_MAX_DIM = 512;

function makeDxSupplierLogoDataUrl(fill, initials) {
  const t = String(initials).slice(0, 2).replace(/[<>&'"]/g, '');
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><rect width="32" height="32" rx="6" fill="${fill}"/><text x="16" y="20" text-anchor="middle" fill="#fff" font-size="10" font-weight="600" font-family="system-ui,sans-serif">${t}</text></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

/** Placeholder suppliers for DX Library (until API is wired). */
const DX_LIBRARY_DUMMY_SUPPLIERS = [
  {
    id: 'acme',
    label: 'Acme Interiors',
    count: 12,
    logoSrc: makeDxSupplierLogoDataUrl('#b45309', 'AC'),
  },
  {
    id: 'northwind',
    label: 'Northwind Living',
    count: 8,
    logoSrc: makeDxSupplierLogoDataUrl('#15803d', 'NW'),
  },
  {
    id: 'urban',
    label: 'Urban Form Co.',
    count: 7,
    logoSrc: makeDxSupplierLogoDataUrl('#1d4ed8', 'UF'),
  },
];

const DX_LIBRARY_DUMMY_CATEGORY_SOURCE = [
  ['Seating', 6],
  ['Tables', 4],
  ['Lighting', 5],
  ['Décor', 4],
  ['Storage', 3],
  ['Other', 5],
];

function sortDxLibraryCategoryEntries(entries) {
  return [...entries].sort((a, b) => {
    const ka = a[0];
    const kb = b[0];
    const aOther = String(ka).trim().toLowerCase() === 'other';
    const bOther = String(kb).trim().toLowerCase() === 'other';
    if (aOther && !bOther) return 1;
    if (bOther && !aOther) return -1;
    return String(ka).localeCompare(String(kb));
  });
}

/**
 * Decode image to canvas and verify at least one pixel has alpha below 255 (transparency / semi-transparency).
 * Fully opaque PNGs fail here. Uses a downscaled sample for performance.
 */
function pngHasAlphaTransparency(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        let w = img.naturalWidth;
        let h = img.naturalHeight;
        if (!w || !h) {
          URL.revokeObjectURL(url);
          resolve(false);
          return;
        }
        let cw = w;
        let ch = h;
        const maxDim = TRANSPARENCY_CHECK_MAX_DIM;
        if (w > maxDim || h > maxDim) {
          const scale = maxDim / Math.max(w, h);
          cw = Math.max(1, Math.floor(w * scale));
          ch = Math.max(1, Math.floor(h * scale));
        }
        const canvas = document.createElement('canvas');
        canvas.width = cw;
        canvas.height = ch;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) {
          URL.revokeObjectURL(url);
          reject(new Error('no_context'));
          return;
        }
        ctx.drawImage(img, 0, 0, cw, ch);
        const { data } = ctx.getImageData(0, 0, cw, ch);
        URL.revokeObjectURL(url);
        for (let i = 3; i < data.length; i += 4) {
          if (data[i] < 255) {
            resolve(true);
            return;
          }
        }
        resolve(false);
      } catch (err) {
        URL.revokeObjectURL(url);
        reject(err);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('decode'));
    };
    img.src = url;
  });
}

function extractGenerateMaskPolygonList(data) {
  if (data == null) return [];
  const tr = data.taskResult;
  if (Array.isArray(tr)) return tr;
  if (Array.isArray(data.polygons)) return data.polygons;
  if (Array.isArray(data)) return data;
  return [];
}

function getNumericRoomObjectIdFromCreateResponse(res) {
  if (res == null || typeof res !== 'object') return null;
  const raw =
    res.id ?? res.objectId ?? res.object_id ?? res.roomObjectId ?? res.room_object_id;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/** Payload for Model Studio “Curated Models” after confirming library picks. */
function mapSavedRoomObjectToCuratedSummary(obj) {
  if (!obj || typeof obj !== 'object') return null;
  const rawUrl = pickRoomObjectImageUrl(obj);
  const thumbSrc =
    rawUrl && (/^https?:\/\//i.test(rawUrl) || rawUrl.startsWith('//'))
      ? resolveRoomObjectImageSrcForDisplay(rawUrl)
      : '';
  return {
    id: obj.id,
    name: obj.name ?? 'Model',
    category: obj.category ?? 'Other',
    thumbSrc,
  };
}

export default function ModelLibraryModal({
  roomId,
  initialStep = 'library',
  /** When opening directly on Detected Models, pre-select these row ids (e.g. canvas "Save as Object"). */
  initialDetectedSelectionIds = null,
  onClose,
  /** My Library: after user checks models and clicks Confirm, parent can show them in Model Studio (e.g. Curated Models). */
  onConfirmLibrarySelection = null,
  /** Room object ids to pre-check in My Library when the modal opens (e.g. Curated Models). */
  initialLibrarySelectionIds = null,
  /**
   * When true, the "Cancel" button in the Detected Models step behaves like a pure close.
   * This prevents switching steps (library) while ImageSelectionPanel is active.
   */
  closeDetectedModelsCancelAsClose = false,
  /** Room object ids to omit from “My Library” while an upload / isolated session is active (parent-owned). */
  excludeLibraryRoomObjectIds = null,
  items,
  setItems,
  onUploadStandardMaskLoading,
  onUploadStandardMaskComplete,
  onUploadStandardMaskError,
}) {
  const inputRef = useRef(null);
  const isolatedInputRef = useRef(null);
  const previewUrlsRef = useRef([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isIsolatedDragOver, setIsIsolatedDragOver] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  /** Which picker to reopen when the user clicks Re-upload after a validation failure. */
  const [validationErrorUploadTarget, setValidationErrorUploadTarget] = useState('standard');
  const [selectedDetectedModelIds, setSelectedDetectedModelIds] = useState([]);
  const [savedRoomObjects, setSavedRoomObjects] = useState([]);
  const [savedRoomObjectsLoading, setSavedRoomObjectsLoading] = useState(false);
  const [savedRoomObjectsError, setSavedRoomObjectsError] = useState('');
  const [brokenLibraryThumbIds, setBrokenLibraryThumbIds] = useState(() => new Set());
  const [selectedLibraryObjectIds, setSelectedLibraryObjectIds] = useState([]);
  const librarySelectionSeededRef = useRef(false);
  /** Library step: 'dx' = DX Library, 'my' = My Library (room objects). */
  const [libraryBrowserTab, setLibraryBrowserTab] = useState('my');
  /** My Library: filter grid by category (null = all). */
  const [libraryCategoryFilter, setLibraryCategoryFilter] = useState(null);
  /** DX Library: selected supplier id (null = all). */
  const [dxLibrarySupplierId, setDxLibrarySupplierId] = useState(null);
  /** DX Library: dummy category key (null = all). */
  const [dxLibraryCategoryKey, setDxLibraryCategoryKey] = useState(null);
  /** When true, deletions run only after footer Confirm (same pattern as object-choice delete flow). */
  const [libraryDeleteMode, setLibraryDeleteMode] = useState(false);
  /** Library step: when true, inline name/category edits are active. */
  const [libraryEditMode, setLibraryEditMode] = useState(false);
  const [editingLibraryObjectId, setEditingLibraryObjectId] = useState(null);
  /** id -> { name, category } for inline edits in library step. */
  const [libraryEdits, setLibraryEdits] = useState({});
  /** Detected Models step: remove-from-session only after footer Confirm delete (mirrors Model Library). */
  const [detectedDeleteMode, setDetectedDeleteMode] = useState(false);
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [refineModalOpen, setRefineModalOpen] = useState(false);
  const [refineItemId, setRefineItemId] = useState(null);
  const [refineBrushSize, setRefineBrushSize] = useState(24);
  const [refineApplying, setRefineApplying] = useState(false);
  const [refineUndoStack, setRefineUndoStack] = useState([]);
  const [refineRedoStack, setRefineRedoStack] = useState([]);
  const [refineCursor, setRefineCursor] = useState({ visible: false, x: 0, y: 0, diameter: 0 });
  const refineCanvasRef = useRef(null);
  const refineWrapRef = useRef(null);
  const refineBaseCanvasRef = useRef(null);
  const refineDrawingRef = useRef(false);
  /** Standard upload flow: optional crop hints added one-by-one (joined with newlines, sent as `prompt`). */
  const [standardUploadCropLines, setStandardUploadCropLines] = useState([]);
  const [standardUploadCropDraft, setStandardUploadCropDraft] = useState('');

  const standardUploadCropPromptCombined = useMemo(
    () => standardUploadCropLines.map((s) => s.trim()).filter(Boolean).join('\n'),
    [standardUploadCropLines]
  );

  const addStandardUploadCropLine = () => {
    const next = standardUploadCropDraft.trim();
    if (!next) return;
    setStandardUploadCropLines((prev) => [...prev, next]);
    setStandardUploadCropDraft('');
  };

  const removeStandardUploadCropLine = (index) => {
    setStandardUploadCropLines((prev) => prev.filter((_, i) => i !== index));
  };

  const hasUploadingItems = useMemo(
    () => items.some((item) => item.status === 'uploading'),
    [items]
  );

  const isolatedModelItems = useMemo(
    () => items.filter((item) => item.uploadSource === 'isolated'),
    [items]
  );

  const libraryExcludeIdSet = useMemo(() => {
    if (!Array.isArray(excludeLibraryRoomObjectIds) || excludeLibraryRoomObjectIds.length === 0) {
      return null;
    }
    return new Set(excludeLibraryRoomObjectIds.map((id) => String(id)));
  }, [excludeLibraryRoomObjectIds]);

  const visibleSavedRoomObjects = useMemo(() => {
    if (!libraryExcludeIdSet) return savedRoomObjects;
    return savedRoomObjects.filter((o) => !libraryExcludeIdSet.has(String(o.id)));
  }, [savedRoomObjects, libraryExcludeIdSet]);

  const libraryCategoryRows = useMemo(() => {
    const counts = new Map();
    visibleSavedRoomObjects.forEach((item) => {
      const cat = String(item.category ?? 'Other').trim() || 'Other';
      counts.set(cat, (counts.get(cat) ?? 0) + 1);
    });
    return [...counts.entries()].sort((a, b) => {
      const ka = a[0];
      const kb = b[0];
      const aOther = String(ka).trim().toLowerCase() === 'other';
      const bOther = String(kb).trim().toLowerCase() === 'other';
      if (aOther && !bOther) return 1;
      if (bOther && !aOther) return -1;
      return String(ka).localeCompare(String(kb));
    });
  }, [visibleSavedRoomObjects]);

  const visibleSavedRoomObjectsForGrid = useMemo(() => {
    if (libraryCategoryFilter == null) return visibleSavedRoomObjects;
    return visibleSavedRoomObjects.filter((item) => {
      const cat = String(item.category ?? 'Other').trim() || 'Other';
      return cat === libraryCategoryFilter;
    });
  }, [visibleSavedRoomObjects, libraryCategoryFilter]);

  const dxLibraryDummyCategoryRows = useMemo(
    () => sortDxLibraryCategoryEntries(DX_LIBRARY_DUMMY_CATEGORY_SOURCE),
    [],
  );

  const dxLibraryDummyAssetTotal = useMemo(
    () => dxLibraryDummyCategoryRows.reduce((sum, [, n]) => sum + n, 0),
    [dxLibraryDummyCategoryRows],
  );

  useEffect(() => {
    if (libraryBrowserTab !== 'dx') {
      setDxLibrarySupplierId(null);
      setDxLibraryCategoryKey(null);
    }
  }, [libraryBrowserTab]);

  useEffect(() => {
    if (libraryCategoryFilter == null) return;
    const labels = new Set(
      visibleSavedRoomObjects.map((item) => String(item.category ?? 'Other').trim() || 'Other'),
    );
    if (!labels.has(libraryCategoryFilter)) setLibraryCategoryFilter(null);
  }, [visibleSavedRoomObjects, libraryCategoryFilter]);

  useEffect(() => {
    setSelectedLibraryObjectIds((prev) => {
      const exclude = libraryExcludeIdSet;
      const next = prev.filter((id) => {
        if (exclude?.has(String(id))) return false;
        return savedRoomObjects.some((o) => String(o.id) === id);
      });
      if (next.length === prev.length && next.every((id, i) => id === prev[i])) return prev;
      return next;
    });
  }, [savedRoomObjects, libraryExcludeIdSet]);

  const detectedStepAutoSelectRef = useRef(false);
  useLayoutEffect(() => {
    if (Array.isArray(initialDetectedSelectionIds) && initialDetectedSelectionIds.length > 0) {
      setSelectedDetectedModelIds(initialDetectedSelectionIds);
      detectedStepAutoSelectRef.current = true;
    }
  }, [initialDetectedSelectionIds]);

  useEffect(() => {
    if (currentStep !== 'detectedModels') {
      detectedStepAutoSelectRef.current = false;
      return;
    }
    if (detectedStepAutoSelectRef.current) return;
    if (!isolatedModelItems.length) return;
    if (selectedDetectedModelIds.length > 0) return;
    setSelectedDetectedModelIds(isolatedModelItems.map((item) => item.id));
    detectedStepAutoSelectRef.current = true;
  }, [currentStep, isolatedModelItems, selectedDetectedModelIds.length]);

  /** Upload-step thumbnail row: only standard uploads; isolated models are listed on Detected Models only. */
  const uploadStepPreviewItems = useMemo(
    () => items.filter((item) => item.uploadSource !== 'isolated'),
    [items]
  );

  useEffect(() => {
    if (!hasUploadingItems) return undefined;
    const timer = window.setInterval(() => {
      setItems((prev) =>
        prev.map((item) => {
          if (item.status !== 'uploading') return item;
          const nextProgress = Math.min(100, item.progress + Math.floor(Math.random() * 16) + 8);
          return {
            ...item,
            progress: nextProgress,
            status: nextProgress >= 100 ? 'completed' : 'uploading',
          };
        })
      );
    }, 220);

    return () => window.clearInterval(timer);
  }, [hasUploadingItems, setItems]);

  const pushUploadedItems = (files, uploadSource = 'standard') => {
    if (!files.length) return;
    const base = Date.now();
    const nextItems = files.map((entry, index) => {
      const file = entry?.file ?? entry;
      const previewUrl = URL.createObjectURL(file);
      previewUrlsRef.current.push(previewUrl);
      const defaultName = file.name.replace(/\.[^/.]+$/, '');
      const needsBackgroundRemoval = Boolean(entry?.needsBackgroundRemoval);
      return {
        id: `${base}-${index}-${Math.random().toString(36).slice(2, 10)}-${file.name}`,
        name: file.name,
        detailsName: defaultName,
        detailsCategory: '',
        file,
        size: file.size,
        previewUrl,
        progress: 0,
        status: 'uploading',
        uploadSource,
        needsBackgroundRemoval,
        backgroundRemovalStatus:
          uploadSource === 'isolated' ? (needsBackgroundRemoval ? 'pending' : 'completed') : 'completed',
        backgroundRemovalError: '',
        saveStatus: uploadSource === 'isolated' ? 'pending' : 'saved',
        saveError: '',
        roomObjectId: null,
      };
    });
    setItems((prev) => [...prev, ...nextItems]);
    return nextItems;
  };

  const setIsolatedItemState = (itemId, patch) => {
    setItems((prev) => prev.map((item) => (item.id === itemId ? { ...item, ...patch } : item)));
  };

  const setBackgroundRemovalState = (itemId, patch) => {
    setItems((prev) => prev.map((item) => (item.id === itemId ? { ...item, ...patch } : item)));
  };

  const runBackgroundRemovalTask = async ({ itemId, roomObjectId, originalFileName }) => {
    try {
      setBackgroundRemovalState(itemId, {
        backgroundRemovalStatus: 'processing',
        backgroundRemovalError: '',
      });
      const runRes = await runAiInference({
        task_name: 'remove_background',
        room_object_id: roomObjectId,
        prompt: '',
      });
      const taskId = runRes?.taskId ?? runRes?.aiTaskId ?? runRes?.id;
      if (taskId == null) {
        throw new Error('Background removal failed to start (missing task id).');
      }
      const resolved = await new Promise((resolve, reject) => {
        addTask(taskId, {
          onResolve: resolve,
          onError: reject,
        });
      });
      const resultKey = resolved?.taskResult?.resultS3Key ?? resolved?.taskResult?.result_s3_key;
      if (!resultKey) {
        throw new Error('Background removal completed without a result image.');
      }
      const presigned = await getAiInferencePresignedUrl(resultKey);
      const url = presigned?.url ?? presigned?.presignedUrl;
      if (!url) {
        throw new Error('Background removal result URL was not returned.');
      }
      const imageResponse = await fetch(url);
      if (!imageResponse.ok) {
        throw new Error(`Failed to download processed image (${imageResponse.status}).`);
      }
      const blob = await imageResponse.blob();
      const processedFile = new File([blob], originalFileName.replace(/\.[^/.]+$/, '.png'), {
        type: 'image/png',
      });
      const nextPreviewUrl = URL.createObjectURL(processedFile);
      previewUrlsRef.current.push(nextPreviewUrl);
      setItems((prev) =>
        prev.map((existing) => {
          if (existing.id !== itemId) return existing;
          if (existing.previewUrl) {
            URL.revokeObjectURL(existing.previewUrl);
            previewUrlsRef.current = previewUrlsRef.current.filter((urlRef) => urlRef !== existing.previewUrl);
          }
          return {
            ...existing,
            file: processedFile,
            size: processedFile.size,
            previewUrl: nextPreviewUrl,
            backgroundRemovalStatus: 'completed',
            backgroundRemovalError: '',
            backgroundResultKey: resultKey,
          };
        })
      );
    } catch (error) {
      setBackgroundRemovalState(itemId, {
        backgroundRemovalStatus: 'failed',
        backgroundRemovalError: error?.message || 'Background removal failed.',
      });
    }
  };

  const runIsolatedItemPipeline = async (item) => {
    if (!roomId) {
      setIsolatedItemState(item.id, {
        saveStatus: 'failed',
        saveError: 'Room ID is missing. Please re-open this panel from a room.',
        backgroundRemovalStatus: item.needsBackgroundRemoval ? 'failed' : item.backgroundRemovalStatus,
      });
      return;
    }
    try {
      setIsolatedItemState(item.id, {
        saveStatus: 'saving',
        saveError: '',
      });
      const createRes = await createRoomObject(roomId, {
        name: 'Model',
        category: 'Other',
        image: item.file,
      });
      const roomObjectIdRaw = createRes?.id ?? createRes?.roomObjectId ?? createRes?.room_object_id;
      const roomObjectId = Number(roomObjectIdRaw);
      if (!Number.isFinite(roomObjectId)) {
        throw new Error('Saved model did not return a room object id.');
      }
      setIsolatedItemState(item.id, {
        saveStatus: 'saved',
        saveError: '',
        roomObjectId,
      });
      if (item.needsBackgroundRemoval) {
        await runBackgroundRemovalTask({
          itemId: item.id,
          roomObjectId,
          originalFileName: item.file?.name || item.name || 'model.png',
        });
      }
    } catch (error) {
      setIsolatedItemState(item.id, {
        saveStatus: 'failed',
        saveError: error?.message || 'Failed to save model.',
        backgroundRemovalStatus: item.needsBackgroundRemoval ? 'failed' : item.backgroundRemovalStatus,
      });
    }
  };

  const appendFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    const accepted = [];
    const nextErrors = [];

    files.forEach((file) => {
      if (!isAcceptedImage(file)) {
        nextErrors.push(`${file.name}: Only JPG and PNG files are allowed.`);
        return;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        nextErrors.push(`${file.name}: File must be 8MB or smaller.`);
        return;
      }
      accepted.push(file);
    });

    if (nextErrors.length) setValidationErrorUploadTarget('standard');
    setValidationErrors(nextErrors);
    pushUploadedItems(accepted);
  };

  const processIsolatedFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    const accepted = [];
    const nextErrors = [];

    for (const file of files) {
      if (!isAcceptedImage(file)) {
        nextErrors.push(`${file.name}: Only JPG and PNG files are allowed.`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        nextErrors.push(`${file.name}: File must be 8MB or smaller.`);
        continue;
      }
      const nameLower = file?.name?.toLowerCase() || '';
      const isPng = nameLower.endsWith('.png') || file.type === 'image/png';
      let needsBackgroundRemoval = true;
      if (isPng) {
        try {
          const hasTransparency = await pngHasAlphaTransparency(file);
          needsBackgroundRemoval = !hasTransparency;
        } catch {
          nextErrors.push(`${file.name}: Could not read this image. Try another JPG or PNG.`);
          continue;
        }
      }
      accepted.push({
        file,
        needsBackgroundRemoval,
      });
    }

    const showIsolatedErrorOverlay = nextErrors.length > 0;
    if (showIsolatedErrorOverlay) setValidationErrorUploadTarget('isolated');
    setValidationErrors(showIsolatedErrorOverlay ? nextErrors : []);
    const addedItems = pushUploadedItems(accepted, 'isolated') || [];
    if (addedItems.length > 0) {
      setSelectedDetectedModelIds((prev) => {
        const next = new Set(prev);
        addedItems.forEach((item) => next.add(item.id));
        return Array.from(next);
      });
    }
    addedItems.forEach((item) => {
      runIsolatedItemPipeline(item);
    });
    if (accepted.length > 0) {
      setCurrentStep('detectedModels');
    }
  };

  const removeUploadItem = (itemId) => {
    setSelectedDetectedModelIds((prev) => prev.filter((id) => id !== itemId));
    setItems((prev) => {
      const target = prev.find((item) => item.id === itemId);
      if (target?.previewUrl) {
        URL.revokeObjectURL(target.previewUrl);
        previewUrlsRef.current = previewUrlsRef.current.filter((url) => url !== target.previewUrl);
      }
      return prev.filter((item) => item.id !== itemId);
    });
  };

  const toggleDetectedModelSelection = (itemId) => {
    setSelectedDetectedModelIds((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    );
  };

  const allDetectedSelected =
    isolatedModelItems.length > 0 &&
    isolatedModelItems.every((item) => selectedDetectedModelIds.includes(item.id));
  const hasNonReadySelectedDetectedModels = isolatedModelItems.some(
    (item) =>
      selectedDetectedModelIds.includes(item.id) &&
      (item.saveStatus !== 'saved' || item.backgroundRemovalStatus !== 'completed')
  );

  const handleSelectAllDetected = () => {
    if (allDetectedSelected) {
      setSelectedDetectedModelIds([]);
      return;
    }
    setSelectedDetectedModelIds(isolatedModelItems.map((item) => item.id));
  };

  const executeDetectedModelsRemove = () => {
    if (!selectedDetectedModelIds.length) return;
    const selectedIdSet = new Set(selectedDetectedModelIds);
    setItems((prev) => {
      const kept = [];
      prev.forEach((item) => {
        if (!selectedIdSet.has(item.id)) {
          kept.push(item);
          return;
        }
        if (item?.previewUrl) {
          URL.revokeObjectURL(item.previewUrl);
          previewUrlsRef.current = previewUrlsRef.current.filter((url) => url !== item.previewUrl);
        }
      });
      return kept;
    });
    setSelectedDetectedModelIds([]);
  };

  const handleRefreshDetectedList = () => {
    const currentDetectedIds = new Set(isolatedModelItems.map((item) => item.id));
    setSelectedDetectedModelIds((prev) => prev.filter((id) => currentDetectedIds.has(id)));
  };

  const activeRefineItem = useMemo(
    () => items.find((item) => item.id === refineItemId) ?? null,
    [items, refineItemId]
  );

  const handleOpenRefineModal = () => {
    if (selectedDetectedModelIds.length !== 1) {
      setSubmitError('Please select exactly one detected model to refine.');
      return;
    }
    const selectedId = selectedDetectedModelIds[0];
    const target = isolatedModelItems.find((item) => item.id === selectedId);
    if (!target?.previewUrl) {
      setSubmitError('The selected model image is unavailable for refining.');
      return;
    }
    setSubmitError('');
    setRefineBrushSize(24);
    setRefineItemId(selectedId);
    setRefineModalOpen(true);
  };

  const closeRefineModal = (force = false) => {
    if (refineApplying && !force) return;
    refineDrawingRef.current = false;
    setRefineModalOpen(false);
    setRefineItemId(null);
    setRefineUndoStack([]);
    setRefineRedoStack([]);
    setRefineCursor({ visible: false, x: 0, y: 0, diameter: 0 });
  };

  const drawRefineCanvas = (canvas) => {
    const base = refineBaseCanvasRef.current;
    if (!canvas || !base) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(base, 0, 0);
  };

  const getRefineCanvasPoint = (event) => {
    const canvas = refineCanvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = rect.width > 0 ? canvas.width / rect.width : 1;
    const scaleY = rect.height > 0 ? canvas.height / rect.height : 1;
    const x = (event.clientX - rect.left) * scaleX;
    const y = (event.clientY - rect.top) * scaleY;
    return { x, y };
  };

  const updateRefineCursor = (event) => {
    const canvas = refineCanvasRef.current;
    const wrap = refineWrapRef.current;
    if (!canvas || !wrap) return;
    const rect = canvas.getBoundingClientRect();
    const wrapRect = wrap.getBoundingClientRect();
    const displayScaleX = rect.width > 0 ? rect.width / canvas.width : 1;
    const diameter = Math.max(8, refineBrushSize * displayScaleX);
    setRefineCursor({
      visible: true,
      x: event.clientX - wrapRect.left + wrap.scrollLeft,
      y: event.clientY - wrapRect.top + wrap.scrollTop,
      diameter,
    });
  };

  const applyErasePoint = (point, moveFrom = null) => {
    const canvas = refineCanvasRef.current;
    if (!canvas || !point) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = 'rgba(0,0,0,1)';
    ctx.fillStyle = 'rgba(0,0,0,1)';
    ctx.lineWidth = refineBrushSize;
    if (moveFrom) {
      ctx.beginPath();
      ctx.moveTo(moveFrom.x, moveFrom.y);
      ctx.lineTo(point.x, point.y);
      ctx.stroke();
    } else {
      ctx.beginPath();
      ctx.arc(point.x, point.y, refineBrushSize / 2, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  };

  const createRefineSnapshot = () => {
    const canvas = refineCanvasRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    return ctx.getImageData(0, 0, canvas.width, canvas.height);
  };

  const restoreRefineSnapshot = (snapshot) => {
    const canvas = refineCanvasRef.current;
    if (!canvas || !snapshot) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.putImageData(snapshot, 0, 0);
  };

  const handleRefineUndo = () => {
    if (refineUndoStack.length === 0) return;
    const current = createRefineSnapshot();
    const previous = refineUndoStack[refineUndoStack.length - 1];
    restoreRefineSnapshot(previous);
    setRefineUndoStack((prev) => prev.slice(0, -1));
    if (current) {
      setRefineRedoStack((prev) => [...prev, current]);
    }
  };

  const handleRefineRedo = () => {
    if (refineRedoStack.length === 0) return;
    const current = createRefineSnapshot();
    const next = refineRedoStack[refineRedoStack.length - 1];
    restoreRefineSnapshot(next);
    setRefineRedoStack((prev) => prev.slice(0, -1));
    if (current) {
      setRefineUndoStack((prev) => [...prev, current]);
    }
  };

  const handleConfirmRefine = async () => {
    const canvas = refineCanvasRef.current;
    const target = activeRefineItem;
    if (!canvas || !target) return;
    setRefineApplying(true);
    try {
      const blob = await new Promise((resolve) => {
        canvas.toBlob((nextBlob) => resolve(nextBlob), 'image/png');
      });
      if (!blob) throw new Error('Failed to export refined image.');
      const baseName =
        target.detailsName?.trim() ||
        target.name?.replace(/\.[^/.]+$/, '')?.trim() ||
        'Refined model';
      const refinedFile = new File([blob], `${baseName.replace(/\s+/g, '-').toLowerCase()}.png`, {
        type: 'image/png',
      });
      const nextPreviewUrl = URL.createObjectURL(refinedFile);
      previewUrlsRef.current.push(nextPreviewUrl);

      let previousPreviewUrl = null;
      setItems((prev) =>
        prev.map((item) => {
          if (item.id !== target.id) return item;
          previousPreviewUrl = item.previewUrl || null;
          return {
            ...item,
            file: refinedFile,
            name: refinedFile.name,
            size: refinedFile.size,
            previewUrl: nextPreviewUrl,
            saveStatus: item.roomObjectId ? 'saving' : item.saveStatus,
            saveError: '',
            backgroundResultKey: undefined,
            backgroundRemovalStatus: 'completed',
            backgroundRemovalError: '',
          };
        })
      );
      if (previousPreviewUrl) {
        URL.revokeObjectURL(previousPreviewUrl);
        previewUrlsRef.current = previewUrlsRef.current.filter((urlRef) => urlRef !== previousPreviewUrl);
      }

      if (roomId && target.roomObjectId) {
        const createRes = await createRoomObject(roomId, {
          name: target.detailsName?.trim() || baseName,
          category: target.detailsCategory?.trim() || 'Other',
          image: refinedFile,
        });
        const roomObjectIdRaw =
          createRes?.id ?? createRes?.roomObjectId ?? createRes?.room_object_id;
        const nextRoomObjectId = Number(roomObjectIdRaw);
        if (!Number.isFinite(nextRoomObjectId)) {
          throw new Error('Refined model did not return a room object id.');
        }
        try {
          await deleteRoomObject(roomId, target.roomObjectId);
        } catch {
          // Best effort: keep flow alive even if old object cleanup fails.
        }
        setIsolatedItemState(target.id, {
          saveStatus: 'saved',
          saveError: '',
          roomObjectId: nextRoomObjectId,
          backgroundRemovalStatus: 'completed',
          backgroundRemovalError: '',
        });
      }
      closeRefineModal(true);
    } catch (error) {
      setSubmitError(error?.message || 'Failed to refine model image.');
      if (target?.id) {
        setIsolatedItemState(target.id, {
          saveStatus: 'saved',
          saveError: '',
          backgroundRemovalStatus: 'completed',
          backgroundRemovalError: '',
        });
      }
    } finally {
      setRefineApplying(false);
    }
  };

  useEffect(() => {
    if (!refineModalOpen || !activeRefineItem?.previewUrl) return;
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      const canvas = refineCanvasRef.current;
      const baseCanvas = refineBaseCanvasRef.current || document.createElement('canvas');
      refineBaseCanvasRef.current = baseCanvas;
      if (!canvas) return;
      const w = Math.max(1, image.naturalWidth || 1);
      const h = Math.max(1, image.naturalHeight || 1);
      canvas.width = w;
      canvas.height = h;
      baseCanvas.width = w;
      baseCanvas.height = h;
      const baseCtx = baseCanvas.getContext('2d');
      if (!baseCtx) return;
      baseCtx.clearRect(0, 0, w, h);
      baseCtx.drawImage(image, 0, 0, w, h);
      drawRefineCanvas(canvas);
      setRefineUndoStack([]);
      setRefineRedoStack([]);
    };
    image.src = activeRefineItem.previewUrl;
  }, [refineModalOpen, activeRefineItem]);

  const toggleLibraryObjectSelection = (objectId) => {
    const key = String(objectId);
    setSelectedLibraryObjectIds((prev) =>
      prev.includes(key) ? prev.filter((id) => id !== key) : [...prev, key]
    );
  };

  const allLibraryObjectsSelected =
    visibleSavedRoomObjectsForGrid.length > 0 &&
    visibleSavedRoomObjectsForGrid.every((o) => selectedLibraryObjectIds.includes(String(o.id)));

  const handleSelectAllLibraryObjects = () => {
    if (allLibraryObjectsSelected) {
      setSelectedLibraryObjectIds([]);
      return;
    }
    setSelectedLibraryObjectIds(visibleSavedRoomObjectsForGrid.map((o) => String(o.id)));
  };

  const executeLibraryObjectsDelete = async () => {
    if (!roomId || selectedLibraryObjectIds.length === 0) return;
    setSavedRoomObjectsLoading(true);
    setSavedRoomObjectsError('');
    try {
      for (const id of selectedLibraryObjectIds) {
        await deleteRoomObject(roomId, id);
      }
      setSelectedLibraryObjectIds([]);
      setLibraryDeleteMode(false);
      const objects = await getRoomObjects(roomId);
      setSavedRoomObjects(objects);
    } catch (error) {
      setSavedRoomObjectsError(error?.message || 'Failed to remove models.');
    } finally {
      setSavedRoomObjectsLoading(false);
    }
  };

  const loadSavedRoomObjects = async () => {
    if (!roomId) {
      setSavedRoomObjects([]);
      setSavedRoomObjectsError('Room ID is missing.');
      return;
    }
    setSavedRoomObjectsLoading(true);
    setSavedRoomObjectsError('');
    try {
      const objects = await getRoomObjects(roomId);
      setSavedRoomObjects(objects);
      setSelectedLibraryObjectIds((prev) =>
        prev.filter((id) => objects.some((o) => String(o.id) === id))
      );
    } catch (error) {
      setSavedRoomObjects([]);
      setSavedRoomObjectsError(error?.message || 'Failed to load models.');
    } finally {
      setSavedRoomObjectsLoading(false);
    }
  };

  const handleConfirmDetectedModels = async () => {
    if (!selectedDetectedModelIds.length) {
      setSubmitError('Please select at least one detected model to save.');
      return;
    }

    const selectedIdSet = new Set(selectedDetectedModelIds);
    const selectedItems = isolatedModelItems.filter((item) => selectedIdSet.has(item.id));
    const nonReadyItem = selectedItems.find(
      (item) => item.saveStatus !== 'saved' || item.backgroundRemovalStatus !== 'completed'
    );
    if (nonReadyItem) {
      const detail =
        nonReadyItem.saveStatus !== 'saved'
          ? nonReadyItem.saveStatus === 'failed'
            ? `Saving failed for "${nonReadyItem.name}".`
            : `Saving is still in progress for "${nonReadyItem.name}".`
          : nonReadyItem.backgroundRemovalStatus === 'failed'
            ? `Background removal failed for "${nonReadyItem.name}".`
            : `Background removal is still processing for "${nonReadyItem.name}".`;
      setSubmitError(`${detail} Please wait for completion or remove this item.`);
      return;
    }

    if (!selectedItems.length) {
      setSubmitError('No valid detected models were selected.');
      return;
    }

    setSubmitError('');
    setSubmitLoading(true);
    try {
      for (const item of selectedItems) {
        if (!roomId || !item.roomObjectId || !item.backgroundResultKey) continue;
        await updateRoomObject(roomId, item.roomObjectId, {
          ai_result_image_s3_key: item.backgroundResultKey,
        });
      }
      setItems((prev) => prev.filter((item) => !selectedIdSet.has(item.id)));
      selectedItems.forEach((item) => {
        if (!item?.previewUrl) return;
        URL.revokeObjectURL(item.previewUrl);
        previewUrlsRef.current = previewUrlsRef.current.filter((url) => url !== item.previewUrl);
      });
      setSelectedDetectedModelIds([]);
      setCurrentStep('library');
    } catch (error) {
      setSubmitError(error?.message || 'Failed to save detected models.');
    } finally {
      setSubmitLoading(false);
    }
  };

  useEffect(() => {
    if (currentStep !== 'library') return;
    loadSavedRoomObjects();
  }, [currentStep, roomId]);

  useEffect(() => {
    librarySelectionSeededRef.current = false;
    setSelectedLibraryObjectIds([]);
    setLibraryDeleteMode(false);
    setLibraryEditMode(false);
    setEditingLibraryObjectId(null);
    setLibraryEdits({});
    setLibraryCategoryFilter(null);
    setDxLibrarySupplierId(null);
    setDxLibraryCategoryKey(null);
  }, [roomId]);

  useEffect(() => {
    if (currentStep !== 'library') {
      librarySelectionSeededRef.current = false;
      setLibraryDeleteMode(false);
      setLibraryEditMode(false);
      setEditingLibraryObjectId(null);
      setLibraryEdits({});
      setLibraryBrowserTab('my');
      setLibraryCategoryFilter(null);
      setDxLibrarySupplierId(null);
      setDxLibraryCategoryKey(null);
    }
    if (currentStep !== 'detectedModels') {
      setDetectedDeleteMode(false);
    }
  }, [currentStep]);

  useLayoutEffect(() => {
    if (currentStep !== 'library') return;
    if (librarySelectionSeededRef.current) return;
    if (savedRoomObjectsLoading) return;

    if (!savedRoomObjects.length) {
      librarySelectionSeededRef.current = true;
      return;
    }

    if (!visibleSavedRoomObjects.length) {
      librarySelectionSeededRef.current = true;
      return;
    }

    librarySelectionSeededRef.current = true;

    if (!Array.isArray(initialLibrarySelectionIds) || initialLibrarySelectionIds.length === 0) {
      return;
    }
    const valid = new Set(visibleSavedRoomObjects.map((o) => String(o.id)));
    const next = initialLibrarySelectionIds.map(String).filter((id) => valid.has(id));
    if (next.length > 0) {
      setSelectedLibraryObjectIds(next);
    }
  }, [currentStep, savedRoomObjects, savedRoomObjectsLoading, initialLibrarySelectionIds, visibleSavedRoomObjects]);

  useEffect(() => {
    setBrokenLibraryThumbIds(new Set());
  }, [savedRoomObjects]);

  const beginLibraryEditMode = (objectId) => {
    if (!visibleSavedRoomObjects.length || objectId == null) return;
    setLibraryDeleteMode(false);
    setLibraryEditMode(true);
    setEditingLibraryObjectId(objectId);
    setSubmitError('');
    setLibraryEdits((prev) => ({
      ...prev,
      [objectId]: prev?.[objectId] || {
        name: visibleSavedRoomObjects.find((obj) => obj.id === objectId)?.name || '',
        category: visibleSavedRoomObjects.find((obj) => obj.id === objectId)?.category || '',
      },
    }));
  };

  const handleLibraryEditFieldChange = (objectId, field, value) => {
    setLibraryEdits((prev) => ({
      ...prev,
      [objectId]: {
        name: prev?.[objectId]?.name ?? visibleSavedRoomObjects.find((o) => o.id === objectId)?.name ?? '',
        category:
          prev?.[objectId]?.category ??
          visibleSavedRoomObjects.find((o) => o.id === objectId)?.category ??
          '',
        [field]: value,
      },
    }));
  };

  const handleSaveLibraryEdits = async () => {
    if (!roomId) {
      setSubmitError('Room ID is missing. Please re-open this panel from a room.');
      return;
    }
    const changes = [];
    visibleSavedRoomObjects.forEach((obj) => {
      const edit = libraryEdits?.[obj.id];
      if (!edit) return;
      const nextName = (edit.name ?? '').trim();
      const nextCategory = (edit.category ?? '').trim();
      const currentName = (obj.name ?? '').trim();
      const currentCategory = (obj.category ?? '').trim();
      if (nextName === currentName && nextCategory === currentCategory) return;
      changes.push({
        id: obj.id,
        name: nextName,
        category: nextCategory,
      });
    });
    if (!changes.length) {
      setLibraryEditMode(false);
      setLibraryEdits({});
      return;
    }
    setSubmitError('');
    setSubmitLoading(true);
    try {
      for (const change of changes) {
        await patchRoomObject(roomId, change.id, {
          name: change.name,
          category: change.category,
        });
      }
      await loadSavedRoomObjects();
      setLibraryEditMode(false);
      setEditingLibraryObjectId(null);
      setLibraryEdits({});
    } catch (error) {
      setSubmitError(error?.message || 'Failed to update model details.');
    } finally {
      setSubmitLoading(false);
    }
  };

  const onInputChange = (event) => {
    appendFiles(event.target.files);
    event.target.value = '';
  };

  const onIsolatedInputChange = async (event) => {
    await processIsolatedFiles(event.target.files);
    event.target.value = '';
  };

  const onDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    appendFiles(event.dataTransfer?.files);
  };

  const onIsolatedDrop = async (event) => {
    event.preventDefault();
    setIsIsolatedDragOver(false);
    await processIsolatedFiles(event.dataTransfer?.files);
  };

  const resolveSavedObjectImageUrl = async (roomObjectId, createRes) => {
    let raw = pickRoomObjectImageUrl(createRes);
    if ((!raw || !String(raw).trim()) && roomId && roomObjectId != null) {
      try {
        const objects = await getRoomObjects(roomId);
        const found = objects.find((o) => Number(o.id) === roomObjectId);
        raw = pickRoomObjectImageUrl(found);
      } catch {
        /* ignore */
      }
    }
    if (!raw || !String(raw).trim()) return '';
    return resolveRoomObjectImageSrcForDisplay(String(raw).trim());
  };

  const runGenerateMaskForRoomObject = async (roomObjectId, labels) => {
    const res = await runAiInference({
      task_name: 'generate_mask',
      room_object_id: roomObjectId,
      labels,
    });
    const immediateList = extractGenerateMaskPolygonList(res);
    const hasImmediatePayload =
      Array.isArray(res?.taskResult) || Array.isArray(res?.polygons) || Array.isArray(res);
    if (hasImmediatePayload) {
      let resultImageUrl = null;
      const tr = res?.taskResult;
      const resultKey =
        (tr && !Array.isArray(tr) && (tr.resultS3Key ?? tr.result_s3_key)) ||
        res?.taskResult?.resultS3Key ||
        res?.taskResult?.result_s3_key;
      if (resultKey) {
        const presigned = await getAiInferencePresignedUrl(resultKey);
        resultImageUrl = presigned?.url ?? presigned?.presignedUrl ?? null;
      }
      return { polygons: immediateList, resultImageUrl };
    }
    const taskId = res?.taskId ?? res?.aiTaskId ?? res?.id;
    if (taskId == null) {
      throw new Error('Scan could not be started.');
    }
    return new Promise((resolve, reject) => {
      addTask(taskId, {
        onResolve: async (data) => {
          try {
            const list = extractGenerateMaskPolygonList(data);
            const rawTr = data?.taskResult ?? data?.polygons;
            const resultKey =
              rawTr && !Array.isArray(rawTr)
                ? rawTr.resultS3Key ?? rawTr.result_s3_key
                : null;
            let resultImageUrl = null;
            if (resultKey) {
              const presigned = await getAiInferencePresignedUrl(resultKey);
              resultImageUrl = presigned?.url ?? presigned?.presignedUrl ?? null;
            }
            resolve({ polygons: list, resultImageUrl });
          } catch (err) {
            reject(err);
          }
        },
        onError: reject,
      });
    });
  };

  /** Standard "Upload image" flow: save immediately, or save + generate_mask when Interior to crop labels exist. */
  const handleConfirmUploadStandard = async () => {
    if (!roomId) {
      setSubmitError('Room ID is missing. Please re-open this panel from a room.');
      return;
    }
    const toSave = items.filter((item) => item.uploadSource !== 'isolated');
    if (!toSave.length) {
      setSubmitError('Please upload at least one image.');
      return;
    }

    const labels = standardUploadCropLines.map((s) => s.trim()).filter(Boolean);
    const hasMaskLabels = labels.length > 0;

    setSubmitError('');
    setSubmitLoading(true);
    if (hasMaskLabels) {
      onUploadStandardMaskLoading?.(true);
    }
    try {
      if (hasMaskLabels) {
        const savedEntries = [];
        for (const item of toSave) {
          const name =
            item.detailsName?.trim() ||
            item.name?.replace(/\.[^/.]+$/, '')?.trim() ||
            'Model';
          const createRes = await createRoomObject(roomId, {
            name,
            category: 'Other',
            image: item.file,
          });
          const roomObjectId = getNumericRoomObjectIdFromCreateResponse(createRes);
          if (roomObjectId == null) {
            throw new Error('Saved model did not return a room object id.');
          }
          const fallbackImageUrl = await resolveSavedObjectImageUrl(roomObjectId, createRes);
          savedEntries.push({ roomObjectId, fallbackImageUrl });
          if (item.previewUrl) {
            URL.revokeObjectURL(item.previewUrl);
            previewUrlsRef.current = previewUrlsRef.current.filter((u) => u !== item.previewUrl);
          }
        }
        setItems((prev) => prev.filter((entry) => entry.uploadSource === 'isolated'));
        setStandardUploadCropLines([]);
        setStandardUploadCropDraft('');
        setSubmitLoading(false);
        onClose();

        const maskErrors = [];
        for (const entry of savedEntries) {
          try {
            const { polygons, resultImageUrl } = await runGenerateMaskForRoomObject(
              entry.roomObjectId,
              labels,
            );
            const imageUrl = resultImageUrl || entry.fallbackImageUrl;
            await Promise.resolve(
              onUploadStandardMaskComplete?.({
                imageUrl,
                polygons,
                roomObjectId: entry.roomObjectId,
              }),
            );
          } catch (maskErr) {
            maskErrors.push(maskErr);
          }
        }
        if (maskErrors.length > 0) {
          const firstError = maskErrors[0];
          onUploadStandardMaskError?.({
            message: firstError?.message || 'Failed to generate masks for one or more uploads.',
          });
        }
      } else {
        for (const item of toSave) {
          const name =
            item.detailsName?.trim() ||
            item.name?.replace(/\.[^/.]+$/, '')?.trim() ||
            'Model';
          await createRoomObject(roomId, {
            name,
            category: 'Other',
            image: item.file,
            prompt: standardUploadCropPromptCombined,
          });
          if (item.previewUrl) {
            URL.revokeObjectURL(item.previewUrl);
            previewUrlsRef.current = previewUrlsRef.current.filter((u) => u !== item.previewUrl);
          }
        }
        setItems((prev) => prev.filter((entry) => entry.uploadSource === 'isolated'));
        setStandardUploadCropLines([]);
        setStandardUploadCropDraft('');
        onClose();
      }
    } catch (error) {
      setSubmitError(error?.message || 'Failed to save models.');
    } finally {
      setSubmitLoading(false);
      if (hasMaskLabels) {
        onUploadStandardMaskLoading?.(false);
      }
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/20"
      role="dialog"
      aria-modal="true"
      aria-labelledby="model-library-title"
    >
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col backdrop-blur-xl backdrop-saturate-150 border border-white/50 bg-white/50">
        <div className="absolute top-0 right-0 p-2 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded text-gray-500 transition-colors hover:text-gray-800"
            aria-label="Close modal"
            title="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="flex items-start justify-start gap-3 px-8 pt-4">
          <h2
            id="model-library-title"
            className="flex min-h-[2.5rem] items-center justify-center text-center text-lg lao-mn text-primary-300"
          >
            {currentStep === 'upload'
              ? 'Upload Models'
              : currentStep === 'detectedModels'
                ? 'Detected Models'
                : 'Model Library'}
          </h2>
        </div>

        <div className="px-8 pb-8 pt-2 flex flex-col gap-3">
        {currentStep === 'library' ? (
          <>
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap gap-2" role="tablist" aria-label="Model library catalogs">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={libraryBrowserTab === 'dx'}
                    id="library-tab-dx"
                    onClick={() => {
                      setLibraryBrowserTab('dx');
                      setLibraryCategoryFilter(null);
                      setLibraryDeleteMode(false);
                      setLibraryEditMode(false);
                      setEditingLibraryObjectId(null);
                      setLibraryEdits({});
                    }}
                    className={`rounded-full border px-4 py-1.5 text-sm font-medium lao-mn transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300/60 ${
                      libraryBrowserTab === 'dx'
                        ? 'border-primary-300 bg-primary-300 text-white'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-primary-200 hover:bg-primary-500'
                    }`}
                    style={{ lineHeight: 1.5 }}
                  >
                    <span className={libraryBrowserTab === 'dx' ? 'text-white' : 'text-primary-200'}>DX</span> Library
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={libraryBrowserTab === 'my'}
                    id="library-tab-my"
                    onClick={() => {
                      setLibraryBrowserTab('my');
                      setLibraryDeleteMode(false);
                      setLibraryEditMode(false);
                      setEditingLibraryObjectId(null);
                      setLibraryEdits({});
                    }}
                    className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300/60 ${
                      libraryBrowserTab === 'my'
                        ? 'border-primary-300 bg-primary-300 text-white'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-primary-200 hover:bg-primary-500'
                    }`}
                    style={{ lineHeight: 1.5 }}
                  >
                    My Library
                  </button>
                </div>
                {libraryBrowserTab === 'my' && (
                  <div className="flex items-center gap-5">
                    <button
                      type="button"
                      onClick={() => setLibraryDeleteMode((v) => !v)}
                      disabled={savedRoomObjectsLoading || libraryEditMode}
                      className={`text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
                        libraryDeleteMode ? 'text-red-700 hover:text-red-800' : 'text-gray-600 hover:text-gray-800'
                      }`}
                    >
                      {libraryDeleteMode ? 'Cancel remove' : 'Remove'}
                    </button>
                    <button
                      type="button"
                      onClick={handleSelectAllLibraryObjects}
                      disabled={visibleSavedRoomObjectsForGrid.length === 0 || savedRoomObjectsLoading || libraryEditMode}
                      className="text-sm font-medium text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {allLibraryObjectsSelected ? 'Unselect All' : 'Select All'}
                    </button>
                    <button
                      type="button"
                      onClick={loadSavedRoomObjects}
                      disabled={savedRoomObjectsLoading || libraryEditMode}
                      className="text-sm font-medium text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Refresh List
                    </button>
                  </div>
                )}
              </div>

              {libraryBrowserTab === 'dx' && (
                <div role="tabpanel" aria-labelledby="library-tab-dx" className="flex min-h-64 flex-row">
                  <div className="flex w-[200px] shrink-0 flex-col rounded-lg border border-r-0 border-gray-200 bg-white/50 min-h-64 max-h-96 mr-2">
                    <div className="shrink-0 px-3 py-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">Suppliers</p>
                    </div>
                    <div className="min-h-0 flex flex-col gap-2 overflow-y-auto p-2">
                      <button
                        type="button"
                        onClick={() => setDxLibrarySupplierId(null)}
                        className={`flex w-full bg-white border rounded-full items-center justify-between gap-2 p-3 text-left text-xs font-medium transition-colors ${
                          dxLibrarySupplierId == null
                            ? 'text-primary-300 hover:border-primary-300 hover:bg-primary-500'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                        aria-pressed={dxLibrarySupplierId == null}
                      >
                        <span>All</span>
                        <span className="shrink-0 tabular-nums text-gray-500 hidden">{dxLibraryDummyAssetTotal}</span>
                      </button>
                      {DX_LIBRARY_DUMMY_SUPPLIERS.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => setDxLibrarySupplierId(s.id)}
                          className={`flex w-full bg-white border rounded-full items-center justify-between gap-2 p-2 text-left text-xs transition-colors ${
                            dxLibrarySupplierId === s.id
                              ? 'text-gray-700 border-primary-300 hover:bg-gray-50'
                              : 'text-primary-300 hover:border-primary-300 hover:bg-primary-500'
                          }`}
                          aria-pressed={dxLibrarySupplierId === s.id}
                          title={s.label}
                        >
                          <span className="flex min-w-0 flex-1 items-center gap-2">
                            <img
                              src={s.logoSrc}
                              alt=""
                              width={28}
                              height={28}
                              draggable={false}
                              className="h-7 w-7 shrink-0 rounded-full border border-gray-200/60 object-cover"
                            />
                            <span className="min-w-0 flex-1 truncate">{s.label}</span>
                          </span>
                          <span className="shrink-0 tabular-nums text-gray-500 hidden">{s.count}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex w-[230px] shrink-0 flex-col rounded-l-lg border border-gray-200 border-l-0 border-r-0 bg-white min-h-64 max-h-96">
                    <div className="shrink-0 border-b border-gray-200 px-3 py-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">Categories</p>
                    </div>
                    <div className="min-h-0 flex-1 overflow-y-auto py-1">
                      <button
                        type="button"
                        onClick={() => setDxLibraryCategoryKey(null)}
                        className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs font-medium transition-colors ${
                          dxLibraryCategoryKey == null
                            ? 'bg-primary-500/15 text-primary-300'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                        aria-pressed={dxLibraryCategoryKey == null}
                      >
                        <span>All</span>
                        <span className="shrink-0 tabular-nums text-gray-500">{dxLibraryDummyAssetTotal}</span>
                      </button>
                      {dxLibraryDummyCategoryRows.map(([category, count]) => (
                        <button
                          key={category}
                          type="button"
                          onClick={() => setDxLibraryCategoryKey(category)}
                          className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs transition-colors ${
                            dxLibraryCategoryKey === category
                              ? 'bg-primary-500/15 font-medium text-primary-300'
                              : 'font-normal text-gray-700 hover:bg-gray-50'
                          }`}
                          aria-pressed={dxLibraryCategoryKey === category}
                          title={category}
                        >
                          <span className="min-w-0 flex-1 truncate">{category}</span>
                          <span className="shrink-0 tabular-nums text-gray-500">{count}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex min-h-64 min-w-0 flex-1 flex-col items-center justify-center overflow-y-auto rounded-r-lg border border-l-0 border-gray-200 bg-white/50 px-6 py-10 text-center h-96 max-h-96">
                    <p className="text-sm font-medium text-gray-700 lao-mn">
                      <span className="text-primary-200">DX</span> Library
                    </p>
                    <p className="mt-2 max-w-sm text-xs text-gray-500">
                      Shared DX assets will appear here. Switch to My Library for models saved to this room.
                    </p>
                    <p className="mt-4 max-w-sm text-xs text-gray-400">
                      Demo filters:{' '}
                      {dxLibrarySupplierId == null
                        ? 'All suppliers'
                        : DX_LIBRARY_DUMMY_SUPPLIERS.find((x) => x.id === dxLibrarySupplierId)?.label ?? '—'}
                      {' · '}
                      {dxLibraryCategoryKey == null ? 'All categories' : dxLibraryCategoryKey}
                    </p>
                  </div>
                </div>
              )}

              {libraryBrowserTab === 'my' && (
                <div
                  role="tabpanel"
                  aria-labelledby="library-tab-my"
                  className="flex min-h-64 flex-row"
                >
                  <div className="flex w-[230px] shrink-0 flex-col rounded-l-lg border border-r-0 border-gray-200 bg-white min-h-64 max-h-96">
                    <div className="shrink-0 border-b border-gray-200 px-3 py-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">Categories</p>
                    </div>
                    <div className="min-h-0 flex-1 overflow-y-auto py-1">
                      <button
                        type="button"
                        onClick={() => setLibraryCategoryFilter(null)}
                        className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs font-medium transition-colors ${
                          libraryCategoryFilter == null
                            ? 'bg-primary-500/15 text-primary-300'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                        aria-pressed={libraryCategoryFilter == null}
                      >
                        <span>All</span>
                        <span className="shrink-0 tabular-nums text-gray-500">{visibleSavedRoomObjects.length}</span>
                      </button>
                      {libraryCategoryRows.map(([category, count]) => (
                        <button
                          key={category}
                          type="button"
                          onClick={() => setLibraryCategoryFilter(category)}
                          className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs transition-colors ${
                            libraryCategoryFilter === category
                              ? 'bg-primary-500/15 font-medium text-primary-300'
                              : 'font-normal text-gray-700 hover:bg-gray-50'
                          }`}
                          aria-pressed={libraryCategoryFilter === category}
                          title={category}
                        >
                          <span className="min-w-0 flex-1 truncate">{category}</span>
                          <span className="shrink-0 tabular-nums text-gray-500">{count}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex min-h-64 min-w-0 flex-1 flex-row flex-wrap gap-2 overflow-y-auto rounded-r-lg border border-gray-200 bg-white/50 p-6 h-96 max-h-96 pr-1">
                  {savedRoomObjectsLoading ? (
                    <p className="text-xs text-gray-500 text-center py-5 w-full">Loading models...</p>
                  ) : savedRoomObjectsError ? (
                    <p className="text-xs text-red-600 text-center py-5 w-full">{savedRoomObjectsError}</p>
                  ) : savedRoomObjects.length === 0 || visibleSavedRoomObjects.length === 0 ? (
                    <p className="text-xs text-gray-500 text-center py-5 w-full">No models</p>
                  ) : visibleSavedRoomObjectsForGrid.length === 0 ? (
                    <p className="text-xs text-gray-500 text-center py-5 w-full">No models in this category.</p>
                  ) : (
                    visibleSavedRoomObjectsForGrid.map((item) => {
                      const rawUrl = pickRoomObjectImageUrl(item);
                      const thumbSrc = resolveRoomObjectImageSrcForDisplay(rawUrl);
                      const thumbBroken = !thumbSrc || brokenLibraryThumbIds.has(item.id);
                      return (
                        <div
                          key={item.id}
                          className="w-fit h-fit flex flex-col items-center gap-2 rounded-xl border border-gray-200 bg-white p-2"
                        >
                          <div
                            className="relative h-20 w-20 shrink-0 overflow-hidden rounded border border-gray-200 bg-[#f9fafb]"
                            style={{
                              backgroundImage:
                                'linear-gradient(45deg, #eff1f3 25%, transparent 25%), linear-gradient(-45deg, #eff1f3 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #eff1f3 75%), linear-gradient(-45deg, transparent 75%, #eff1f3 75%)',
                              backgroundSize: '8px 8px',
                              backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
                            }}
                          >
                            {thumbBroken ? (
                              <div className="absolute inset-0 bg-gray-100/90" aria-hidden="true" />
                            ) : (
                              <img
                                src={thumbSrc}
                                alt={item.name || 'Model'}
                                className="h-full w-full object-contain bg-transparent"
                                draggable={false}
                                referrerPolicy="no-referrer"
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  setBrokenLibraryThumbIds((prev) => {
                                    const next = new Set(prev);
                                    next.add(item.id);
                                    return next;
                                  });
                                }}
                              />
                            )}
                            <input
                              type="checkbox"
                              checked={selectedLibraryObjectIds.includes(String(item.id))}
                              onChange={() => toggleLibraryObjectSelection(item.id)}
                              disabled={savedRoomObjectsLoading}
                              className="pr-object-checkbox absolute bottom-0.5 right-0.5 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                              aria-label={`Select ${item.name || 'model'}`}
                            />
                          </div>
                          <div className="flex-1 flex flex-col gap-1">
                            {libraryEditMode && editingLibraryObjectId === item.id ? (
                              <>
                                <input
                                  type="text"
                                  className="w-20 border-b border-gray-300 bg-white text-[10px] text-center text-gray-800 focus:outline-none"
                                  value={libraryEdits?.[item.id]?.name ?? item.name ?? ''}
                                  onChange={(event) =>
                                    handleLibraryEditFieldChange(item.id, 'name', event.target.value)
                                  }
                                  placeholder="Name"
                                />
                                <input
                                  type="text"
                                  className="w-20 border-b border-gray-300 bg-white text-[10px] text-center text-gray-800 focus:outline-none"
                                  value={libraryEdits?.[item.id]?.category ?? item.category ?? ''}
                                  onChange={(event) =>
                                    handleLibraryEditFieldChange(item.id, 'category', event.target.value)
                                  }
                                  placeholder="Category"
                                />
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  onClick={() => beginLibraryEditMode(item.id)}
                                  className="block w-full text-left text-xs font-medium text-gray-800 truncate hover:text-primary-300"
                                >
                                  {item.name || 'Model'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => beginLibraryEditMode(item.id)}
                                  className="block w-full text-left text-[11px] text-gray-500 hover:text-primary-300"
                                >
                                  {item.category || 'Other'}
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                  </div>
                </div>
              )}
            </div>

            <div className="pt-1 flex items-center justify-end gap-2">
              {libraryBrowserTab === 'my' && !libraryEditMode && !closeDetectedModelsCancelAsClose && (
                <button
                  type="button"
                  onClick={() => {
                    setCurrentStep('upload');
                    setSubmitError('');
                  }}
                  className="flex flex-row justify-center items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-primary-300 hover:bg-primary-500 hover:border-primary-300 hover:text-primary-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-200 disabled:hover:text-primary-300"
                >
                  <svg
                    className="w-4 h-4"
                    viewBox="0 0 15 15"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.125"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M9.5625 0.5625L1.7625 0.562499C1.3425 0.562499 1.1325 0.562499 0.972 0.644249C0.830884 0.716153 0.716153 0.830884 0.644249 0.971999C0.562499 1.1325 0.5625 1.3425 0.5625 1.761L0.5625 9.5625M6.5625 8.8125L8.8125 8.8125M8.8125 8.8125L11.0625 8.8125M8.8125 8.8125L8.8125 6.5625M8.8125 8.8125L8.8125 11.0625M5.9625 3.5625L11.6625 3.5625C12.5025 3.5625 12.9225 3.5625 13.2435 3.726C13.5262 3.87 13.755 4.09875 13.899 4.3815C14.0625 4.7025 14.0625 5.1225 14.0625 5.9625L14.0625 11.6625C14.0625 12.5025 14.0625 12.9225 13.899 13.2435C13.7552 13.5257 13.5257 13.7552 13.2435 13.899C12.9225 14.0625 12.5025 14.0625 11.6625 14.0625L5.9625 14.0625C5.1225 14.0625 4.7025 14.0625 4.3815 13.899C4.09927 13.7552 3.86981 13.5257 3.726 13.2435C3.5625 12.9225 3.5625 12.504 3.5625 11.6655L3.5625 5.96025C3.5625 5.12175 3.5625 4.70175 3.726 4.3815C3.86981 4.09927 4.09927 3.86981 4.3815 3.726C4.7025 3.5625 5.1225 3.5625 5.9625 3.5625Z" />
                  </svg>
                  Add New
                </button>
              )}
              {libraryBrowserTab === 'my' && !(closeDetectedModelsCancelAsClose && !libraryDeleteMode && !libraryEditMode) && (
              <button
                type="button"
                disabled={
                  savedRoomObjectsLoading ||
                  submitLoading ||
                  (libraryDeleteMode && selectedLibraryObjectIds.length === 0)
                }
                onClick={async () => {
                  if (libraryEditMode) {
                    await handleSaveLibraryEdits();
                    return;
                  }
                  if (libraryDeleteMode) {
                    await executeLibraryObjectsDelete();
                    return;
                  }
                  const idSet = new Set(selectedLibraryObjectIds.map(String));
                  const selectedObjs = visibleSavedRoomObjects.filter((o) => idSet.has(String(o.id)));
                  const summaries = selectedObjs
                    .map(mapSavedRoomObjectToCuratedSummary)
                    .filter(Boolean);
                  onConfirmLibrarySelection?.(summaries);
                  setSelectedLibraryObjectIds([]);
                  onClose();
                }}
                className={`flex flex-row justify-center items-center gap-2 rounded-full border px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  libraryDeleteMode
                    ? 'border-gray-800 bg-gray-800 text-white hover:bg-gray-700 disabled:hover:bg-gray-800'
                    : libraryEditMode
                      ? 'border border-gray-200 bg-white text-primary-300 hover:bg-primary-500 hover:border-primary-300 hover:text-primary-300 disabled:hover:bg-white disabled:hover:border-gray-200 disabled:hover:text-primary-300'
                      : 'min-w-[10rem] flex-1 border-0 bg-primary-400 px-4 py-2.5 text-white shadow-sm hover:bg-gray-700 sm:flex-none'
                }`}
              >
                <svg className="w-4 h-4" viewBox="0 0 14 14" fill="none" stroke="currentColor" aria-hidden="true">
                  <path
                    d="M12.5625 6.5625C12.5625 7.35043 12.4073 8.13065 12.1058 8.8586C11.8043 9.58656 11.3623 10.248 10.8051 10.8051C10.248 11.3623 9.58656 11.8043 8.8586 12.1058C8.13065 12.4073 7.35043 12.5625 6.5625 12.5625C5.77457 12.5625 4.99435 12.4073 4.2664 12.1058C3.53845 11.8043 2.87701 11.3623 2.31986 10.8051C1.76271 10.248 1.32075 9.58656 1.01922 8.8586C0.717695 8.13065 0.5625 7.35043 0.5625 6.5625C0.5625 4.9712 1.19464 3.44508 2.31986 2.31986C3.44508 1.19464 4.9712 0.5625 6.5625 0.5625C8.1538 0.5625 9.67992 1.19464 10.8051 2.31986C11.9304 3.44508 12.5625 4.9712 12.5625 6.5625Z"
                    strokeWidth="1.125"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M4.34033 6.98445L5.34367 8.13178C5.40969 8.20734 5.49192 8.26701 5.58422 8.30636C5.67652 8.34571 5.77651 8.36372 5.87674 8.35904C5.97697 8.35436 6.07485 8.32712 6.16308 8.27934C6.25132 8.23157 6.32763 8.16449 6.38633 8.08312L8.78433 4.76245"
                    strokeWidth="1.125"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {libraryDeleteMode
                  ? `Confirm delete (${selectedLibraryObjectIds.length} selected)`
                  : libraryEditMode
                    ? 'Confirm'
                    : `Confirm (${selectedLibraryObjectIds.length} selected)`}
              </button>
              )}
              <button
                type="button"
                onClick={() => {
                  if (libraryEditMode) {
                    setLibraryEditMode(false);
                    setEditingLibraryObjectId(null);
                    setLibraryEdits({});
                    setSubmitError('');
                    return;
                  }
                  setLibraryDeleteMode(false);
                  onClose();
                }}
                className="flex flex-row justify-center items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-primary-300 hover:bg-primary-500 hover:border-primary-300 hover:text-primary-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-200 disabled:hover:text-primary-300"
                aria-label={closeDetectedModelsCancelAsClose && !libraryEditMode ? 'Close' : 'Cancel'}
              >
                <svg className="w-4 h-4" viewBox="0 0 14 14" fill="none" stroke="currentColor" aria-hidden="true">
                  <path
                    d="M8.5625 8.5625L4.5625 4.5625M8.5625 4.5625L4.5625 8.5625M6.5625 12.5625C5.77457 12.5625 4.99435 12.4073 4.2664 12.1058C3.53844 11.8043 2.87701 11.3623 2.31986 10.8051C1.76271 10.248 1.32075 9.58655 1.01922 8.8586C0.717693 8.13065 0.562499 7.35043 0.562499 6.5625C0.562499 5.77457 0.717693 4.99435 1.01922 4.2664C1.32075 3.53845 1.76271 2.87701 2.31986 2.31986C2.87701 1.76271 3.53844 1.32075 4.2664 1.01922C4.99435 0.717694 5.77457 0.5625 6.5625 0.5625C8.1538 0.5625 9.67992 1.19464 10.8051 2.31986C11.9304 3.44508 12.5625 4.9712 12.5625 6.5625C12.5625 8.1538 11.9304 9.67992 10.8051 10.8051C9.67992 11.9304 8.1538 12.5625 6.5625 12.5625Z"
                    strokeWidth="1.125"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {closeDetectedModelsCancelAsClose && !libraryEditMode ? 'Close' : 'Cancel'}
              </button>
            </div>
          </>
        ) : currentStep === 'detectedModels' ? (
          <>
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm text-gray-600">
                {`The system has successfully detected ${isolatedModelItems.length} model${isolatedModelItems.length === 1 ? '' : 's'}.`}
              </p>
              <div className="flex items-center gap-5">
                <button
                  type="button"
                  onClick={() => setDetectedDeleteMode((v) => !v)}
                  disabled={submitLoading}
                  className={`text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
                    detectedDeleteMode ? 'text-red-700 hover:text-red-800' : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  {detectedDeleteMode ? 'Cancel remove' : 'Remove'}
                </button>
                <button
                  type="button"
                  onClick={handleSelectAllDetected}
                  disabled={isolatedModelItems.length === 0 || submitLoading}
                  className="text-sm font-medium text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {allDetectedSelected ? 'Unselect All' : 'Select All'}
                </button>
                <button
                  type="button"
                  onClick={handleRefreshDetectedList}
                  disabled={submitLoading}
                  className="text-sm font-medium text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Refresh List
                </button>
              </div>
            </div>

            <div className="flex flex-row gap-2 h-96 max-h-64 overflow-y-auto pr-1 rounded-lg border border-gray-200 bg-white/50 p-2">
              {isolatedModelItems.length === 0 ? (
                <p className="text-xs text-gray-500 text-center py-5">No isolated models in this session.</p>
              ) : (
                isolatedModelItems.map((item) => (
                  <div
                    key={item.id}
                    className="relative w-fit h-fit flex flex-col items-center gap-2 rounded border border-gray-200 bg-white px-2 py-1.5"
                  >
                    <div
                      className="relative h-20 w-20 shrink-0 overflow-hidden rounded"
                      style={{
                        backgroundColor: '#f9fafb',
                        backgroundImage:
                          'linear-gradient(45deg, #eff1f3 25%, transparent 25%), linear-gradient(-45deg, #eff1f3 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #eff1f3 75%), linear-gradient(-45deg, transparent 75%, #eff1f3 75%)',
                        backgroundSize: '12px 12px',
                        backgroundPosition: '0 0, 0 6px, 6px -6px, -6px 0px',
                      }}
                    >
                      <img
                        src={item.previewUrl}
                        alt={item.name}
                        className="h-full w-full object-contain"
                      />
                      {(item.backgroundRemovalStatus === 'pending' ||
                        item.backgroundRemovalStatus === 'processing') && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <span
                            className="h-8 w-8 rounded-full border-2 border-white/40 border-t-white animate-spin"
                            aria-label="Background removal processing"
                          />
                        </div>
                      )}
                      <input
                        type="checkbox"
                        checked={selectedDetectedModelIds.includes(item.id)}
                        onChange={() => toggleDetectedModelSelection(item.id)}
                        disabled={submitLoading}
                        className="pr-object-checkbox absolute bottom-1 right-1 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label={`Select ${item.name}`}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-gray-800 truncate max-w-20">{item.name}</p>
                      {item.saveStatus === 'saving' && (
                        <p className="text-[10px] text-gray-500">Saving model...</p>
                      )}
                      {item.saveStatus === 'failed' && (
                        <p className="text-[10px] text-red-600">Failed to save model</p>
                      )}
                      {item.backgroundRemovalStatus === 'processing' && (
                        <p className="text-[10px] text-gray-500">Removing background...</p>
                      )}
                      {item.backgroundRemovalStatus === 'failed' && (
                        <p className="text-[10px] text-red-600">Background removal failed</p>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {submitError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {submitError}
              </div>
            )}

            <div className="pt-1 flex flex-wrap items-center justify-between gap-2">
              <button
                type="button"
                disabled={submitLoading || selectedDetectedModelIds.length !== 1}
                onClick={handleOpenRefineModal}
                className="flex flex-row justify-center items-center gap-2 rounded-full border px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-gray-200 bg-white text-primary-300 hover:bg-primary-500 hover:border-primary-300 hover:text-primary-300 disabled:hover:bg-white disabled:hover:border-gray-200 disabled:hover:text-primary-300"
              >
                Refine
              </button>

              <div className="flex flex-row gap-2">
                <button
                  type="button"
                  disabled={
                    submitLoading ||
                    selectedDetectedModelIds.length === 0 ||
                    hasNonReadySelectedDetectedModels
                  }
                  onClick={async () => {
                    if (detectedDeleteMode) {
                      executeDetectedModelsRemove();
                      setDetectedDeleteMode(false);
                      setSubmitError('');
                      return;
                    }
                    await handleConfirmDetectedModels();
                  }}
                  className={`flex flex-row justify-center items-center gap-2 rounded-full border px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    detectedDeleteMode
                      ? 'border-gray-800 bg-gray-800 text-white hover:bg-gray-700 disabled:hover:bg-gray-800'
                      : 'border border-gray-200 bg-white text-primary-300 hover:bg-primary-500 hover:border-primary-300 hover:text-primary-300 disabled:hover:bg-white disabled:hover:border-gray-200 disabled:hover:text-primary-300'
                  }`}
                >
                  <svg className="w-4 h-4" viewBox="0 0 14 14" fill="none" stroke="currentColor" aria-hidden="true">
                    <path d="M12.5625 6.5625C12.5625 7.35043 12.4073 8.13065 12.1058 8.8586C11.8043 9.58656 11.3623 10.248 10.8051 10.8051C10.248 11.3623 9.58656 11.8043 8.8586 12.1058C8.13065 12.4073 7.35043 12.5625 6.5625 12.5625C5.77457 12.5625 4.99435 12.4073 4.2664 12.1058C3.53845 11.8043 2.87701 11.3623 2.31986 10.8051C1.76271 10.248 1.32075 9.58656 1.01922 8.8586C0.717695 8.13065 0.5625 7.35043 0.5625 6.5625C0.5625 4.9712 1.19464 3.44508 2.31986 2.31986C3.44508 1.19464 4.9712 0.5625 6.5625 0.5625C8.1538 0.5625 9.67992 1.19464 10.8051 2.31986C11.9304 3.44508 12.5625 4.9712 12.5625 6.5625Z" strokeWidth="1.125" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M4.34033 6.98445L5.34367 8.13178C5.40969 8.20734 5.49192 8.26701 5.58422 8.30636C5.67652 8.34571 5.77651 8.36372 5.87674 8.35904C5.97697 8.35436 6.07485 8.32712 6.16308 8.27934C6.25132 8.23157 6.32763 8.16449 6.38633 8.08312L8.78433 4.76245" strokeWidth="1.125" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {submitLoading && !detectedDeleteMode
                    ? 'Saving...'
                    : detectedDeleteMode
                      ? `Confirm delete (${selectedDetectedModelIds.length} selected)`
                      : `Confirm (${selectedDetectedModelIds.length} selected)`}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDetectedDeleteMode(false);
                    if (closeDetectedModelsCancelAsClose) {
                      onClose();
                      return;
                    }
                    setCurrentStep('library');
                    setValidationErrors([]);
                    setSubmitError('');
                  }}
                  className="flex flex-row justify-center items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-primary-300 hover:bg-primary-500 hover:border-primary-300 hover:text-primary-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-200 disabled:hover:text-primary-300"
                >
                  <svg className="w-4 h-4" viewBox="0 0 14 14" fill="none" stroke="currentColor" aria-hidden="true">
                    <path d="M8.5625 8.5625L4.5625 4.5625M8.5625 4.5625L4.5625 8.5625M6.5625 12.5625C5.77457 12.5625 4.99435 12.4073 4.2664 12.1058C3.53844 11.8043 2.87701 11.3623 2.31986 10.8051C1.76271 10.248 1.32075 9.58655 1.01922 8.8586C0.717693 8.13065 0.562499 7.35043 0.562499 6.5625C0.562499 5.77457 0.717693 4.99435 1.01922 4.2664C1.32075 3.53845 1.76271 2.87701 2.31986 2.31986C2.87701 1.76271 3.53844 1.32075 4.2664 1.01922C4.99435 0.717694 5.77457 0.5625 6.5625 0.5625C8.1538 0.5625 9.67992 1.19464 10.8051 2.31986C11.9304 3.44508 12.5625 4.9712 12.5625 6.5625C12.5625 8.1538 11.9304 9.67992 10.8051 10.8051C9.67992 11.9304 8.1538 12.5625 6.5625 12.5625Z" strokeWidth="1.125" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {closeDetectedModelsCancelAsClose ? 'Close' : 'Cancel'}
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <p className="text-xs text-gray-600">
              Add images of the models you wish to include in the library by either uploading files or taking photos.
              DX Interiors AI will automatically remove the background.
            </p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
              <div className="flex min-w-0 flex-col gap-2">
                <div
                  onDragOver={(event) => {
                    event.preventDefault();
                    setIsDragOver(true);
                  }}
                  onDragLeave={() => setIsDragOver(false)}
                  onDrop={onDrop}
                  onClick={() => inputRef.current?.click()}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    inputRef.current?.click();
                  }}
                  role="button"
                  tabIndex={0}
                  className={`border-[4px] border-dashed rounded-[26px] p-8 transition-colors backdrop-blur-xl backdrop-saturate-150 cursor-pointer ${
                    isDragOver
                      ? 'border-primary-400/80 bg-primary-100/45'
                      : 'border-white/50 bg-white/35 hover:border-white/70'
                  }`}
                >
                  <div className="flex min-h-[180px] flex-col items-center justify-center text-center">
                    <svg
                      className="mb-4 h-12 w-12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M3 17v1.5c0 1.1.4 2.1 1.2 2.8.7.7 1.7 1.2 2.8 1.2h10c1.1 0 2.1-.4 2.8-1.2.7-.7 1.2-1.7 1.2-2.8V17" />
                      <path d="M12 16V3" />
                      <path d="M16 7l-4-4-4 4" />
                    </svg>
                    <p className="mb-2 text-lg font-semibold text-gray-800">Upload image</p>
                    <div className="text-center text-xs text-gray-500">
                      <p>Maximum file size: 8mb</p>
                      <p>Supported Files: JPG and PNG</p>
                    </div>
                  </div>
                  <input
                    ref={inputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                    multiple
                    className="hidden"
                    onChange={onInputChange}
                  />
                </div>
              </div>

              <div className="flex min-w-0 flex-col gap-2">
                <div
                  onDragOver={(event) => {
                    event.preventDefault();
                    setIsIsolatedDragOver(true);
                  }}
                  onDragLeave={() => setIsIsolatedDragOver(false)}
                  onDrop={onIsolatedDrop}
                  onClick={() => isolatedInputRef.current?.click()}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    isolatedInputRef.current?.click();
                  }}
                  role="button"
                  tabIndex={0}
                  className={`cursor-pointer rounded-[26px] border-[4px] border-dashed p-8 backdrop-blur-xl backdrop-saturate-150 transition-colors ${
                    isIsolatedDragOver
                      ? 'border-primary-400/80 bg-primary-100/45'
                      : 'border-white/50 bg-white/35 hover:border-white/70'
                  }`}
                  aria-label="Isolated model upload"
                >
                  <div className="flex min-h-[180px] flex-col items-center justify-center rounded-xl px-2 py-4 text-center">
                    <svg
                      className="mb-4 h-12 w-12 text-gray-700"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M3 17v1.5c0 1.1.4 2.1 1.2 2.8.7.7 1.7 1.2 2.8 1.2h10c1.1 0 2.1-.4 2.8-1.2.7-.7 1.2-1.7 1.2-2.8V17" />
                      <path d="M12 16V3" />
                      <path d="M16 7l-4-4-4 4" />
                    </svg>
                    <p className="mb-2 text-lg font-semibold text-gray-800">Isolated model</p>
                    <div className="text-xs text-gray-500">
                      <p>Maximum file size: 8mb</p>
                      <p>Supported Files: JPG and PNG</p>
                    </div>
                  </div>
                  <input
                    ref={isolatedInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                    multiple
                    className="hidden"
                    onChange={onIsolatedInputChange}
                  />
                </div>
              </div>
            </div>

            {validationErrors.length > 0 && (
              <div
                className="fixed inset-0 z-[120] flex items-center justify-center p-4"
                role="dialog"
                aria-modal="true"
                aria-labelledby="upload-error-title"
              >
                <div className="w-full max-w-md rounded-xl shadow-md border-dashed rounded-[16px] backdrop-blur-xl backdrop-saturate-150 bg-white/30 flex flex-col items-center justify-center min-h-[300px]">
                  <div className="p-4 mb-4 flex flex-col items-center justify-center">
                    <svg
                      className="w-14 h-14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#FF0000"
                      strokeWidth="1"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M12 6v7" />
                      <path d="M12 17v.01" />
                      <path d="M22 12a10 10 0 11-20 0 10 10 0 0120 0z" />
                    </svg>
                    <p id="upload-error-title" className="mt-2 text-sm font-medium text-gray-800 text-center">
                      {validationErrors.length === 1
                        ? 'This file could not be added'
                        : 'Some files could not be added'}
                    </p>
                    <ul className="mt-3 max-h-40 w-full max-w-sm overflow-y-auto text-left text-xs text-gray-600 list-disc pl-5 space-y-1">
                      {validationErrors.map((message, index) => (
                        <li key={`${index}-${message.slice(0, 24)}`}>{message}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex items-center justify-end gap-2 p-4">
                    <button
                      type="button"
                      onClick={() => {
                        setValidationErrors([]);
                        const ref =
                          validationErrorUploadTarget === 'isolated' ? isolatedInputRef : inputRef;
                        ref.current?.click();
                      }}
                      className="rounded-full bg-gray-800 px-4 py-2 text-white hover:bg-gray-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <svg
                        className="w-4 h-4"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M3 17v1c0 .8.3 1.6.9 2.1.5.6 1.3.9 2.1.9h12c.8 0 1.6-.3 2.1-.9.6-.5.9-1.3.9-2.1v-1" />
                        <path d="M12 16V3" />
                        <path d="M16 7l-4-4-4 4" />
                      </svg>
                      Re-upload
                    </button>
                    <button
                      type="button"
                      onClick={() => setValidationErrors([])}
                      className="rounded-full bg-white px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                    >
                      <svg
                        className="w-4 h-4"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M15 15l-6-6M15 9l-6 6" />
                        <path d="M22 12a10 10 0 11-20 0 10 10 0 0120 0z" />
                      </svg>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {submitError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {submitError}
              </div>
            )}

            <div className="max-h-52 overflow-x-auto overflow-y-hidden pr-1 flex flex-row gap-2">
              {uploadStepPreviewItems.length === 0 ? (
                <p className="text-xs text-gray-500 text-center py-3 w-full hidden"></p>
              ) : (
                uploadStepPreviewItems.map((item) => (
                  <div key={item.id} className="rounded-lg border border-gray-200 bg-white px-3 py-2 shrink-0">
                    <div className="flex items-start gap-2">
                      <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded border border-gray-200">
                        <img src={item.previewUrl} alt={item.name} className="h-full w-full object-cover" />
                        {item.progress < 100 && (
                          <div className="absolute inset-0 flex flex-col justify-end bg-black/45">
                            <div className="px-1 pb-1">
                              <div className="h-1 rounded-full bg-white/35 overflow-hidden">
                                <div
                                  className="h-full bg-white transition-all duration-200"
                                  style={{ width: `${item.progress}%` }}
                                />
                              </div>
                              <div className="mt-0.5 text-center text-[9px] font-medium text-white">
                                {item.progress}%
                              </div>
                            </div>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => removeUploadItem(item.id)}
                          className="absolute top-0.5 right-0.5 h-4 w-4 rounded-full bg-black/65 text-white flex items-center justify-center hover:bg-black/80 transition-colors"
                          aria-label={`Remove ${item.name}`}
                          title="Remove image"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="10"
                            height="10"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {uploadStepPreviewItems.length > 0 && (
              <div className="flex flex-col gap-2 pt-2">
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-medium text-gray-700">Object to crop</span>
                  <span className="text-[10px] text-gray-500">
                    Add at least one object (e.g., “Chair”, “Table”). Confirm is disabled until you do.
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    id="standard-upload-crop-draft"
                    type="text"
                    value={standardUploadCropDraft}
                    onChange={(event) => setStandardUploadCropDraft(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        addStandardUploadCropLine();
                      }
                    }}
                    placeholder="Type an object, then add…"
                    disabled={submitLoading}
                    className="min-w-0 flex-1 rounded-full border border-gray-300 bg-white px-3 py-2 text-xs text-gray-800 placeholder:text-gray-400 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                  />
                  <button
                    type="button"
                    onClick={addStandardUploadCropLine}
                    disabled={submitLoading || !standardUploadCropDraft.trim()}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Add object to crop"
                    title="Add"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                  </button>
                </div>
                {standardUploadCropLines.length > 0 && (
                  <ul className="flex flex-row gap-1.5">
                    {standardUploadCropLines.map((line, index) => (
                      <li
                        key={`${index}-${line.slice(0, 32)}`}
                        className="flex items-start gap-2 rounded-full border border-gray-200 bg-white/80 px-2 py-1.5 text-xs text-gray-800"
                      >
                        <span className="min-w-0 flex-1 break-words">{line}</span>
                        <button
                          type="button"
                          onClick={() => removeStandardUploadCropLine(index)}
                          disabled={submitLoading}
                          className="shrink-0 rounded p-0.5 text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                          aria-label="Remove"
                          title="Remove"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {items.length > 0 && (
              <div className="pt-1 flex items-center justify-between gap-2">

                <p className="text-xs font-semibold text-gray-500 max-w-sm">Important: For faster image processing and auto-background removal, please do not mix images with isolated, simple, and complex backgrounds in a single upload.</p>

                <div className="flex flex-row gap-2">
                  <button
                    type="button"
                    disabled={submitLoading || standardUploadCropLines.length === 0}
                    onClick={() => {
                      void handleConfirmUploadStandard();
                    }}
                    className="flex flex-row justify-center items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-primary-300 hover:bg-primary-500 hover:border-primary-300 hover:text-primary-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-200 disabled:hover:text-primary-300"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 14 14" fill="none" stroke="currentColor" aria-hidden="true">
                      <path d="M12.5625 6.5625C12.5625 7.35043 12.4073 8.13065 12.1058 8.8586C11.8043 9.58656 11.3623 10.248 10.8051 10.8051C10.248 11.3623 9.58656 11.8043 8.8586 12.1058C8.13065 12.4073 7.35043 12.5625 6.5625 12.5625C5.77457 12.5625 4.99435 12.4073 4.2664 12.1058C3.53845 11.8043 2.87701 11.3623 2.31986 10.8051C1.76271 10.248 1.32075 9.58656 1.01922 8.8586C0.717695 8.13065 0.5625 7.35043 0.5625 6.5625C0.5625 4.9712 1.19464 3.44508 2.31986 2.31986C3.44508 1.19464 4.9712 0.5625 6.5625 0.5625C8.1538 0.5625 9.67992 1.19464 10.8051 2.31986C11.9304 3.44508 12.5625 4.9712 12.5625 6.5625Z" strokeWidth="1.125" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M4.34033 6.98445L5.34367 8.13178C5.40969 8.20734 5.49192 8.26701 5.58422 8.30636C5.67652 8.34571 5.77651 8.36372 5.87674 8.35904C5.97697 8.35436 6.07485 8.32712 6.16308 8.27934C6.25132 8.23157 6.32763 8.16449 6.38633 8.08312L8.78433 4.76245" strokeWidth="1.125" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {submitLoading ? 'Saving...' : 'Confirm'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCurrentStep('library');
                      setValidationErrors([]);
                      setSubmitError('');
                      setStandardUploadCropLines([]);
                      setStandardUploadCropDraft('');
                    }}
                    className="flex flex-row justify-center items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-primary-300 hover:bg-primary-500 hover:border-primary-300 hover:text-primary-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-200 disabled:hover:text-primary-300"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 14 14" fill="none" stroke="currentColor" aria-hidden="true">
                      <path d="M8.5625 8.5625L4.5625 4.5625M8.5625 4.5625L4.5625 8.5625M6.5625 12.5625C5.77457 12.5625 4.99435 12.4073 4.2664 12.1058C3.53844 11.8043 2.87701 11.3623 2.31986 10.8051C1.76271 10.248 1.32075 9.58655 1.01922 8.8586C0.717693 8.13065 0.562499 7.35043 0.562499 6.5625C0.562499 5.77457 0.717693 4.99435 1.01922 4.2664C1.32075 3.53845 1.76271 2.87701 2.31986 2.31986C2.87701 1.76271 3.53844 1.32075 4.2664 1.01922C4.99435 0.717694 5.77457 0.5625 6.5625 0.5625C8.1538 0.5625 9.67992 1.19464 10.8051 2.31986C11.9304 3.44508 12.5625 4.9712 12.5625 6.5625C12.5625 8.1538 11.9304 9.67992 10.8051 10.8051C9.67992 11.9304 8.1538 12.5625 6.5625 12.5625Z" strokeWidth="1.125" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </>
        )}
        </div>
      </div>
      {refineModalOpen && (
        <div className="absolute inset-0 z-[130] flex items-center justify-center bg-black/25 p-4">
          <div className="w-full max-w-3xl rounded-xl bg-white/30 shadow-2xl backdrop-blur">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <h3 className="text-sm font-semibold text-gray-800">Refine Model Image</h3>
              <button
                type="button"
                onClick={closeRefineModal}
                disabled={refineApplying}
                className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 disabled:opacity-50"
                aria-label="Close refine editor"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="grid justify-items-center items-center gap-4 p-4">
              <div
                ref={refineWrapRef}
                className="relative overflow-auto rounded-lg border border-gray-300 overflow-hidden"
                style={{
                  backgroundColor: '#f9fafb',
                  backgroundImage:
                    'linear-gradient(45deg, #eff1f3 25%, transparent 25%), linear-gradient(-45deg, #eff1f3 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #eff1f3 75%), linear-gradient(-45deg, transparent 75%, #eff1f3 75%)',
                  backgroundSize: '12px 12px',
                  backgroundPosition: '0 0, 0 6px, 6px -6px, -6px 0px',
                }}
              >
                <canvas
                  ref={refineCanvasRef}
                  className="mx-auto block max-h-[68vh] max-w-full h-auto w-auto touch-none cursor-none"
                  onPointerEnter={(event) => updateRefineCursor(event)}
                  onPointerDown={(event) => {
                    const point = getRefineCanvasPoint(event);
                    if (!point) return;
                    updateRefineCursor(event);
                    const snapshot = createRefineSnapshot();
                    if (snapshot) setRefineUndoStack((prev) => [...prev, snapshot]);
                    setRefineRedoStack([]);
                    refineDrawingRef.current = true;
                    refineCanvasRef.current?.setPointerCapture?.(event.pointerId);
                    applyErasePoint(point);
                    refineCanvasRef.current.dataset.lastX = String(point.x);
                    refineCanvasRef.current.dataset.lastY = String(point.y);
                  }}
                  onPointerMove={(event) => {
                    updateRefineCursor(event);
                    if (!refineDrawingRef.current) return;
                    const point = getRefineCanvasPoint(event);
                    if (!point) return;
                    const lastX = Number(refineCanvasRef.current?.dataset.lastX ?? NaN);
                    const lastY = Number(refineCanvasRef.current?.dataset.lastY ?? NaN);
                    const prevPoint =
                      Number.isFinite(lastX) && Number.isFinite(lastY) ? { x: lastX, y: lastY } : null;
                    applyErasePoint(point, prevPoint);
                    refineCanvasRef.current.dataset.lastX = String(point.x);
                    refineCanvasRef.current.dataset.lastY = String(point.y);
                  }}
                  onPointerUp={(event) => {
                    refineDrawingRef.current = false;
                    updateRefineCursor(event);
                    refineCanvasRef.current?.releasePointerCapture?.(event.pointerId);
                  }}
                  onPointerLeave={() => {
                    refineDrawingRef.current = false;
                    setRefineCursor((prev) => ({ ...prev, visible: false }));
                  }}
                />
                {refineCursor.visible && (
                  <div
                    className="pointer-events-none absolute z-20 rounded-full border border-primary-300/90 bg-primary-300/10 shadow-[0_0_0_1px_rgba(255,255,255,0.85)]"
                    style={{
                      width: `${refineCursor.diameter}px`,
                      height: `${refineCursor.diameter}px`,
                      left: `${refineCursor.x}px`,
                      top: `${refineCursor.y}px`,
                      transform: 'translate(-50%, -50%)',
                    }}
                  />
                )}
              </div>
              <div
                className="w-fit flex items-center gap-2 px-4 py-2 rounded-full border border-white bg-[rgba(253,253,253,0.1)] shadow-[0_5px_20px_rgba(0,0,0,0.18)] backdrop-blur-xl backdrop-saturate-150"
                onClick={(event) => event.stopPropagation()}
              >
                <button
                  type="button"
                  disabled={refineApplying}
                  className="p-2 rounded-full bg-primary-300 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Eraser tool"
                >
                  <svg className="w-4 h-4" viewBox="0 0 17 17" fill="currentColor" aria-hidden="true">
                    <path d="M12.1211 0.505946L16.4022 4.77838C17.0768 5.46162 17.0768 6.55135 16.4022 7.23459L8.45405 15.1827C7.80405 15.8302 6.92396 16.1937 6.00649 16.1937C5.08901 16.1937 4.20892 15.8302 3.55892 15.1827L0.505946 12.1297C-0.168649 11.4465 -0.168649 10.3568 0.505946 9.67351L9.67351 0.505946C10.3568 -0.168649 11.4465 -0.168649 12.1211 0.505946ZM1.72541 10.9016L4.78703 13.9546C5.46162 14.6378 6.55135 14.6378 7.23459 13.9546L10.2876 10.9016L6.00649 6.62054L1.72541 10.9016Z" />
                  </svg>
                </button>
                <div
                  className="group flex items-center gap-2 rounded-full bg-white h-[36px] px-3 py-1.5 text-primary-300 transition-colors hover:text-white hover:ring-white/20"
                  title="Eraser size"
                >
                  <input
                    id="refine-brush-size"
                    type="range"
                    min="6"
                    max="120"
                    step="1"
                    value={refineBrushSize}
                    onChange={(event) => setRefineBrushSize(Number(event.target.value))}
                    className="brush-size-slider w-[7.25rem] min-w-[6.5rem] shrink-0"
                    style={{
                      '--brush-fill': `${((refineBrushSize - 6) / (120 - 6)) * 100}%`,
                    }}
                    disabled={refineApplying}
                    aria-label="Eraser size"
                    title={`Eraser size: ${refineBrushSize}`}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleRefineUndo}
                  disabled={refineApplying || refineUndoStack.length === 0}
                  className={`p-2 rounded-full bg-white ${refineUndoStack.length > 0 ? 'text-black hover:bg-primary-300 hover:text-white' : 'text-gray-500 cursor-not-allowed opacity-50'}`}
                  title="Undo"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" clipRule="evenodd" d="M7.5303 3.4697a.75.75 0 010 1.0606L5.8107 6.25H15a5 5 0 015 5 5 5 0 01-5 5H8a.75.75 0 010-1.5h7a3.5 3.5 0 003.5-3.5A3.5 3.5 0 0015 7.75H5.8107l1.7196 1.7197a.75.75 0 01-1.0606 1.0606l-3-3a.75.75 0 010-1.0606l3-3a.75.75 0 011.0606 0z" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={handleRefineRedo}
                  disabled={refineApplying || refineRedoStack.length === 0}
                  className={`p-2 rounded-full bg-white ${refineRedoStack.length > 0 ? 'text-black hover:bg-primary-300 hover:text-white' : 'text-gray-500 cursor-not-allowed opacity-50'}`}
                  title="Redo"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" clipRule="evenodd" d="M16.4697 3.4697a.75.75 0 011.0606 0l3 3a.75.75 0 010 1.0606l-3 3a.75.75 0 11-1.0606-1.0606L18.1893 8.5H9A3.5 3.5 0 005.5 12 3.5 3.5 0 009 15.5h7a.75.75 0 010 1.5H9A5 5 0 014 12a5 5 0 015-5h9.1893l-1.7196-1.7197a.75.75 0 010-1.0606z" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const snapshot = createRefineSnapshot();
                    if (snapshot) setRefineUndoStack((prev) => [...prev, snapshot]);
                    setRefineRedoStack([]);
                    drawRefineCanvas(refineCanvasRef.current);
                  }}
                  disabled={refineApplying}
                  className="p-2 rounded-full bg-white text-black hover:bg-primary-300 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Reset"
                >
                  <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" aria-hidden="true">
                    <path d="M14.0487 4.42713C12.9771 1.97993 10.3359 0.578125 7.48842 0.578125C3.88652 0.578125 0.925868 3.31246 0.577148 6.81351" strokeWidth="1.1547" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M10.9609 4.73559H14.017C14.0716 4.73569 14.1258 4.72501 14.1763 4.70416C14.2268 4.68332 14.2727 4.65272 14.3114 4.6141C14.3501 4.57549 14.3808 4.52963 14.4017 4.47915C14.4226 4.42867 14.4334 4.37455 14.4334 4.31989V1.27148M0.961914 10.5861C2.03348 13.0333 4.67466 14.4351 7.52215 14.4351C11.124 14.4351 14.0847 11.7007 14.4334 8.19969" strokeWidth="1.1547" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M4.04972 10.2773H0.99361C0.938956 10.2772 0.884818 10.2879 0.834295 10.3088C0.783772 10.3296 0.737856 10.3602 0.699174 10.3988C0.660492 10.4374 0.629803 10.4833 0.608865 10.5338C0.587926 10.5843 0.577148 10.6384 0.577148 10.693V13.7414" strokeWidth="1.1547" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <div className="mx-1 h-5 w-px bg-white/45" aria-hidden="true" />
                <button
                  type="button"
                  onClick={closeRefineModal}
                  disabled={refineApplying}
                  className="p-2 rounded-full bg-white text-black hover:bg-primary-300 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Cancel"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={handleConfirmRefine}
                  disabled={refineApplying}
                  className="p-2 rounded-full bg-white text-black hover:bg-primary-300 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  title={refineApplying ? 'Saving...' : 'Confirm'}
                >
                  {refineApplying ? (
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-30" />
                      <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
